import { Koi } from '@ksyos/koi';
import type { IAsset } from '../assets';
import { BezoekAanSpecialistOption } from '../../enums/bezoekAanSpecialistOption';
import { ConclusieDiagnoseOption } from '../../enums/conclusieDiagnoseOption';
import { emailSchema } from '../schemas/emailSchema';
import { phoneNumberNoErrorSchema } from '../schemas/phoneNumberSchema';

export interface IGtoProcedureCustomControlData {
    areThereAdditionalComplaintsOrSymptons: string;
    areThereAdditionalComplaintsOrSymptonsRemark: string;
    areYouBeingTreated: string;
    areYouBeingTreatedRemark: string;
    areYouOnMedication: string;
    areYouOnMedicationRemark: string;
    complaints: string;
    complaintsDuration: string;
    complaintsLocation: string;
    complaintsSeverity: string;
    doYouHaveFever: string;
    doYouHaveFeverRemark: string;
    email?: string;
    files?: IAsset[];
    haveYouEverBeenHospitalized: string;
    haveYouEverBeenHospitalizedRemark: string;
    organizationName?: string;
    phoneNumber: string;
    practitionerName?: string;
}

export const gtoFinalProcedure = Koi.object().keys({
    areThereAdditionalComplaintsOrSymptons: Koi.string().valid('Ja', 'Nee'),
    areThereAdditionalComplaintsOrSymptonsRemark: Koi.when('areThereAdditionalComplaintsOrSymptons', {
        is: 'Ja',
        otherwise: Koi.string().valid(''),
        then: Koi.string().max(1000),
    }),
    areYouBeingTreated: Koi.string().valid('Ja', 'Nee'),
    areYouBeingTreatedRemark: Koi.when('areYouBeingTreated', {
        is: 'Ja',
        otherwise: Koi.string().valid(''),
        then: Koi.string().max(1000),
    }),
    areYouOnMedication: Koi.string().valid('Ja', 'Nee'),
    areYouOnMedicationRemark: Koi.when('areYouOnMedication', {
        is: 'Ja',
        otherwise: Koi.string().valid(''),
        then: Koi.string().max(1000),
    }),
    complaints: Koi.string().max(1000),
    complaintsDuration: Koi.string().max(255),
    complaintsLocation: Koi.string().max(1000),
    complaintsSeverity: Koi.string().max(1000),
    doYouHaveFever: Koi.string().valid('Ja', 'Nee'),
    doYouHaveFeverRemark: Koi.when('doYouHaveFever', {
        is: 'Ja',
        otherwise: Koi.string().valid(''),
        then: Koi.string().max(1000),
    }),
    email: emailSchema.optional(),
    files: Koi.array().items(
        Koi.object().keys({
            filename: Koi.string().max(255),
            s3Id: Koi.string().uuid(),
        })
    ),
    haveYouEverBeenHospitalized: Koi.string().valid('Ja', 'Nee'),
    haveYouEverBeenHospitalizedRemark: Koi.when('haveYouEverBeenHospitalized', {
        is: 'Ja',
        otherwise: Koi.string().valid(''),
        then: Koi.string().max(1000),
    }),
    organizationName: Koi.string().max(255).optional(),
    phoneNumber: phoneNumberNoErrorSchema,
    practitionerName: Koi.string().max(255).optional(),
});

export const gtoV1DraftRequest = Koi.object().keys({
    emailAddress: emailSchema.allow(null),
    toestemming: Koi.boolean().allow(null),
});

export const gtoV1FinalRequest = gtoV1DraftRequest.concat(
    Koi.object().keys({
        emailAddress: emailSchema,
        toestemming: Koi.boolean().valid(true),
    })
);

export const gtcV1FinalRequest = Koi.object().keys({
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
    toelichting: Koi.string().max(1000).allow(null),
    vraag: Koi.string().max(1000),
});

export const gtcV1DraftRequest = gtcV1FinalRequest.concat(
    Koi.object({
        conclusie_diagnose: Koi.any().allow(null),
        vraag: Koi.any().allow(null),
    })
);

export const gtcV1FinalDiagnosticReport = Koi.object().keys({
    beleid: Koi.string().max(1000),
    bevindingen: Koi.string().max(1000),
    bezoek_aan_specialist_nu_nodig: Koi.koi().enum(BezoekAanSpecialistOption),
    toelichting: Koi.string().max(1000).allow(null),
    videoconsult: Koi.string().valid('Ja', 'Nee'),
});

export const gtcV1DraftDiagnosticReport = gtcV1FinalDiagnosticReport.concat(
    Koi.object({
        beleid: Koi.any().allow(null),
        bevindingen: Koi.any().allow(null),
        bezoek_aan_specialist_nu_nodig: Koi.any().allow(null),
        videoconsult: Koi.any().allow(null),
    })
);

export const gtoV1FinalDiagnosticReport = Koi.object().keys({
    bevindingen: Koi.string().max(1000),
    toelichting: Koi.string().max(1000).allow(null),
    videoconsult: Koi.string().valid('Ja', 'Nee'),
});

export const gtoV1DraftDiagnosticReport = gtoV1FinalDiagnosticReport.concat(
    Koi.object().keys({
        bevindingen: Koi.any().allow(null),
        toelichting: Koi.any().allow(null),
        videoconsult: Koi.any().allow(null),
    })
);
